// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import Select2, { DataSelect2 } from 'wdna-select2';

import { GridApi, RowDataChangedEvent, GridReadyEvent, RowNode } from 'wdna-grid-table/lib/types';
import Table, { TableButtonsConfig } from 'wdna-components/lib/table/Table';
import ContainerModal from 'wdna-components/lib/modals/generics/ContainerModal';
import { TabConfig, addTab, removeTab } from 'wdna-components/lib/tabs';
import { messageError, messageInfo, messageBoxWarning, messageBoxInfo, } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import Edit from '../forms/installers/Edit';

import { installer, InstallerDO } from '../../requests';
import { authorization } from '../../../../services/user/requests';

type AuthUser = {
  id: number;
  name: string;
  email: string;
}

type Props = unknown;

interface State {
  userId?: number;
  update?: any;
  loading: boolean;
  data?: InstallerDO[];
  showGrid?: boolean;
  showSelector?: boolean;
  marked?: number;
  userList: AuthUser[];
}

export default class Installers extends React.Component <Props, State> {
  private buttons: TableButtonsConfig;
  private gridApi: GridApi | undefined;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.mounted = false;
    this.buttons = {
      // toolbar
      add: true,
      // actions
      del: true,
      toggleItem: true,
    };

    this.state = {
      update: window.getStr(),
      loading: false,
      data: [],

      marked: 0,

      showGrid: false,
      showSelector: false,
      userList: [],
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load().catch(() => { /* pass */ });
  };

  public shouldComponentUpdate = (nextProps: Props, nextState: State): boolean => {
    return this.state.update !== nextState.update;
  }

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = async (): Promise<void> => {
    this.loadingStart();
    try {
      const installerArray: InstallerDO[] = await installer<InstallerDO[]>('list') || [];
      const userList: any[] = await authorization<AuthUser[]>('users') || [];
      if (this.mounted) {
        this.setState({
          data: installerArray,
          userList,
          marked: 0,
        });
      }
    } catch (error) {
      messageError('Error', getErrorMessage(error));
    }
    this.loadingFinish();
  };

  public loadingStart = (): void => {
    this.setState({
      update: window.getStr(),
      loading: true,
    });
  };

  public loadingFinish = (): void => {
    this.setState({
      update: window.getStr(),
      loading: false,
    });
  };

  public onAdd = (): void => {
    this.setState({
      update: window.getStr(),
      showSelector: true,
    });
  };

  public onClose = (): void => {
    this.setState({
      update: window.getStr(),
      showSelector: false,
    });
  };

  public onSave = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const data: State = _.cloneDeep(this.state);

    const req: Record<string, any> = {
      ...data,
      active: true,
    };

    this.loadingStart();
    installer('set', undefined, req)
      ?.then(() => {
        messageInfo('Success', 'Installer successfully added', 5000);
        this.load().catch(() => { /* pass */ });
      })
      .catch((error: any) => {
        messageError('Error', getErrorMessage(error));
        this.load().catch(() => { /* pass */ });
      })
      .finally(() => {
        this.loadingFinish();
        this.onClose();
      });
  };

  public loadTab = (rowId: number): void => {
    const tabId: string = 'IE';
    const tabData: TabConfig = {
      id: tabId,
      active: true,
      title: 'Installers Edit',
      component: Edit,
      props: {
        rowId,
        report: 'INSTALLERS',
        title: 'Installers',
        notify: this.notify,
      },
    };
    window.store.dispatch(addTab(tabData));
  };

  public notify = (): void => {
    this.load().catch(() => { /* pass */ });
  };

  public onEdit = (value: InstallerDO): void => {
    window.store.dispatch(removeTab('IE'));
    const rowId: number = value.id;
    setTimeout(() => {
      this.loadTab(rowId);
    }, 0);
  };

  public onDelete = (value: InstallerDO): void => {
    messageBoxWarning('Delete', 'Do you want to delete this register?', () => {
      this.loadingStart();
      installer('del', String(value.id))
        ?.then(() => {
          messageInfo('Success', 'Register successfully deleted', 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    });
  };

  public rowDataChanged = (_event: RowDataChangedEvent): void => {
    this.setState({
      update: window.getStr(),
      showGrid: true,
    });
  };

  public handleSelect = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const { value, name } = e.target;
    const { userList } = this.state;
    if (value && name === 'userId') {
      const userFound: AuthUser | undefined = userList.find((user: AuthUser) => user.id === +value);
      if (userFound) {
        this.setState((prevState: State) => ({
          ...prevState,
          userId: userFound.id,
          name: userFound.name,
          email: userFound.email,
        }));
      }
    }
  };

  public countSelected = (): void => {
    let marked: number = 0;
    if (this.gridApi) {
      marked = this.gridApi.getSelectedNodes().length;
    }
    this.setState({
      marked,
    });
  };

  public activateSelected = (): void => {
    messageBoxInfo('Activate', 'Are you sure you want to activate the selected installers?', () => {
      this.bulkActivation(true);
    });
  };

  public deactivateSelected = (): void => {
    messageBoxInfo('Deactivate', 'Are you sure you want to deactivate the selected installers?', () => {
      this.bulkActivation(false);
    });
  };

  public bulkActivation = (changeToActive: boolean): void => {
    if (this.gridApi) {
      const selectedNodes: RowNode[] = this.gridApi.getSelectedNodes();
      const promises: any[] = [];

      // TODO: refactor ???
      selectedNodes.forEach((row: RowNode) => {
        if (!row.data || !row.data.id) return;
        const newData: InstallerDO = {
          id: row.data.id,
          active: changeToActive,
          name: row.data.name,
          email: row.data.email,
          userId: row.data.userId
        };

        promises.push(installer('set', undefined, newData));
      });

      this.loadingStart();
      Promise.all(promises)
        .then(() => {
          messageInfo('Success', `Installers successfully ${changeToActive ? 'activated' : 'deactivated'}`, 5000);
          this.load().catch(() => { /* pass */ });
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public onGridReady = (event: GridReadyEvent): void => {
    // map the api
    this.gridApi = event.api;
  };

  public render(): JSX.Element {
    const { loading, data, marked, showGrid, userList, userId } = this.state;
    const { showSelector } = this.state || false;

    const columnAlias: any = {
      id: 'ID',
      active: 'Active',
      login: 'Username',
      email: 'Email',
      userId: 'User Id',
      name: 'Full name',
    };
    const columnsToHide: any = ['password', 'groupId'];
    const columnStyles: any = {
      id: { textAlign: 'right' },
      active: { textAlign: 'center' },
      login: { textAlign: 'left' },
      email: { textAlign: 'left' },
      userId: { textAlign: 'left' },
      name: { textAlign: 'left' },
      actionButtons: { textAlign: 'center' },

    };
    const selectRowProps: any = {
      mode: 'checkbox',
    };
    const customColumnsDefs: any = {
      id: {
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        suppressSizeToFit: false,
        minWidth: 80,
        maxWidth: 80
      },
    };

    return (
      <div id="content">
        <div className="row">
          <div className="col-6 wdna-padding">
            <span>
              <b>{`Total Rows Selected: ${marked || 0}`}</b>
            </span>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <button type="button" className="btn btn-success" onClick={this.activateSelected}>
              <b>Activate selected installers</b>
            </button>
            &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
            <button type="button" className="btn btn-danger" onClick={this.deactivateSelected}>
              <b>Deactivate selected installers</b>
            </button>
          </div>
        </div>
        <div style={{ visibility: (showGrid ? 'visible' : 'hidden') }}>
          <Table
            data={data || []}
            // TODO: review
            // selectRow={selectRowProps}
            alias={columnAlias}
            hiddenCols={columnsToHide}
            customCellStyles={columnStyles}
            buttons={this.buttons}
            onAdd={this.onAdd}
            onDelete={this.onDelete}
            loading={loading}
            rowDataChanged={this.rowDataChanged}
            onGridReady={this.onGridReady}
            onSelectionChanged={this.countSelected}
            customColumnsDefs={customColumnsDefs}
            selection="multiple"
          />
        </div>

        <ContainerModal
          fullScreenButton={false}
          show={showSelector}
          title="Select the installer's user"
          onClose={this.onClose}
          modalClass="modal-small"
          footerButtons={[
            {
              text: 'Save',
              className: 'btn btn-primary',
              onClick: this.onSave,
            },
          ]}
        >
          <Select2
            name="userId"
            data={userList.map((item: AuthUser): DataSelect2 => {
              return { text: item.name, id: item.id };
            })}
            value={userId}
            options={{
              placeholder: 'Select user',
            }}
            onChange={this.handleSelect}
            style={{ width: '80%' }}
          />
        </ContainerModal>
      </div>
    );
  }
}
